import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Coffee, Flame, Moon, Sunrise } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@/components/ui/card';
import { GLASS_CARD } from '@/components/ui/glass';
import { MESES } from '@/components/constants/meses';
import { useHorasPico } from '@/components/hooks/useHorasPico';
import { cargarListaTrabajadores } from '@/components/hooks/useTrabajadores';
import { useChartColors, useDarkMode, useIsMobile } from './chartHooks';

interface Props {
  empresaId: string;
  mes: number;
  anio: number;
  usuarioId?: string;
  filtrarTrabajadores?: boolean;
}

type Periodo = 1 | 3;

type Columna =
  | { tipo: 'hora'; hora: number }
  | { tipo: 'descanso'; desde: number; hasta: number };

interface Activa {
  dia: number;
  hora: number;
  x: number;
  y: number;
}

const CLAVES_DIA = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
const DIAS = [1, 2, 3, 4, 5, 6, 7];

const hh = (h: number) => `${String(h).padStart(2, '0')}:00`;
const capitalizar = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const minutos = (s: string) => {
  const [h, m] = s.split(':').map(Number);
  return h * 60 + (m || 0);
};

const contarDias = (desde: string, hasta: string) => {
  const n: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
  if (!desde || !hasta) return n;
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const d = new Date(`${desde}T00:00:00`);
  let fin = new Date(`${hasta}T00:00:00`);
  if (fin > hoy && d <= hoy) fin = hoy;
  while (d <= fin) {
    n[((d.getDay() + 6) % 7) + 1] += 1;
    d.setDate(d.getDate() + 1);
  }
  return n;
};

const Dato = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) => (
  <div className="flex items-center gap-2.5 rounded-xl bg-gray-50/80 dark:bg-white/5 px-3 py-2 min-w-0">
    <span className="shrink-0">{icon}</span>
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-gray-400 dark:text-gray-500 truncate">
        {label}
      </div>
      <div className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white truncate">
        {value}
      </div>
    </div>
  </div>
);

const DashboardHorasPico = ({
  empresaId,
  mes,
  anio,
  usuarioId,
  filtrarTrabajadores = false,
}: Props) => {
  const { t } = useTranslation();
  const dark = useDarkMode();
  const isMobile = useIsMobile();
  const colors = useChartColors(dark);
  const [periodo, setPeriodo] = useState<Periodo>(1);
  const [activa, setActiva] = useState<Activa | null>(null);
  const contRef = useRef<HTMLDivElement>(null);
  const [trabajadores, setTrabajadores] = useState<{ id: string; nombre: string }[]>([]);
  const [trabajadorSel, setTrabajadorSel] = useState<string | null>(null);

  useEffect(() => {
    if (!filtrarTrabajadores || !empresaId) return;
    let cancelado = false;
    cargarListaTrabajadores(empresaId).then((lista) => {
      if (cancelado) return;
      const unicos = new Map<string, string>();
      lista.forEach((t) => unicos.set(t.id_usuario, t.nombre || t.email));
      setTrabajadores(
        [...unicos]
          .map(([id, nombre]) => ({ id, nombre }))
          .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
      );
    });
    return () => {
      cancelado = true;
    };
  }, [filtrarTrabajadores, empresaId]);

  const usuarioFiltro = usuarioId ?? trabajadorSel ?? undefined;

  const { loading, error, celdas, horario, desde, hasta, capacidad, totalCitas } = useHorasPico(
    empresaId,
    mes,
    anio,
    periodo,
    usuarioFiltro
  );

  const nombreDia = (d: number) =>
    capitalizar(t(`dashboardCharts.days.${CLAVES_DIA[d - 1]}`).toLowerCase());
  const nombreDiaCorto = (d: number) => nombreDia(d).slice(0, 3);
  const nombreMes = (m: number) => capitalizar(MESES[(m + 11) % 12]);

  const diasPeriodo = useMemo(() => contarDias(desde, hasta), [desde, hasta]);

  const datos = useMemo(() => {
    const mapa = new Map<string, number>();
    celdas.forEach((c) => mapa.set(`${c.dia}-${c.hora}`, c.minutos));
    const valor = (d: number, h: number) => mapa.get(`${d}-${h}`) ?? 0;

    const capMapa = new Map<string, number>();
    capacidad.forEach((c) => capMapa.set(`${c.dia}-${c.hora}`, c.trabajadores));
    const hayCapacidad = capacidad.length > 0;
    const trabajadores = (d: number, h: number) => {
      const n = capMapa.get(`${d}-${h}`) ?? 0;
      return n > 0 ? n : valor(d, h) > 0 ? 1 : 0;
    };

    const hayHorario = hayCapacidad || horario.length > 0;
    const abierto = (d: number, h: number) =>
      hayCapacidad
        ? (capMapa.get(`${d}-${h}`) ?? 0) > 0
        : !hayHorario ||
          horario.some(
            (tr) =>
              tr.dia === d && minutos(tr.inicio) < (h + 1) * 60 && minutos(tr.fin) > h * 60
          );

    const disponible = (d: number, h: number) =>
      60 * (diasPeriodo[d] || 0) * (hayCapacidad ? trabajadores(d, h) : 1);
    const pct = (d: number, h: number) => {
      const disp = disponible(d, h);
      return disp > 0 ? Math.min(1, valor(d, h) / disp) : 0;
    };

    const candidatas = new Set<number>(celdas.map((c) => c.hora));
    capacidad.forEach((c) => candidatas.add(c.hora));
    horario.forEach((tr) => {
      const ini = Math.floor(minutos(tr.inicio) / 60);
      const fin = Math.ceil(minutos(tr.fin) / 60);
      for (let h = ini; h < fin; h++) candidatas.add(h);
    });
    const horas = [...candidatas].sort((a, b) => a - b);

    const columnas: Columna[] = [];
    horas.forEach((h, i) => {
      if (i > 0 && h - horas[i - 1] > 1) {
        columnas.push({ tipo: 'descanso', desde: horas[i - 1] + 1, hasta: h });
      }
      columnas.push({ tipo: 'hora', hora: h });
    });

    const totalMinutos = celdas.reduce((a, c) => a + c.minutos, 0);

    let pico: { dia: number; hora: number; pct: number } | null = null;
    celdas.forEach((c) => {
      const p = pct(c.dia, c.hora);
      if (p > 0 && (!pico || p > pico.pct)) pico = { dia: c.dia, hora: c.hora, pct: p };
    });

    const porHora = horas.map((h) => {
      const abiertos = DIAS.filter((d) => abierto(d, h) && disponible(d, h) > 0);
      const ocupado = abiertos.reduce((a, d) => a + valor(d, h), 0);
      const disp = abiertos.reduce((a, d) => a + disponible(d, h), 0);
      return {
        hora: h,
        abierta: abiertos.length > 0,
        ocupado,
        disp,
        pct: disp > 0 ? Math.min(1, ocupado / disp) : 0,
      };
    });

    let franja: { desde: number; pct: number } | null = null;
    for (let i = 0; i < porHora.length - 1; i++) {
      const a = porHora[i];
      const b = porHora[i + 1];
      if (b.hora !== a.hora + 1 || !a.abierta || !b.abierta) continue;
      const p = (a.ocupado + b.ocupado) / (a.disp + b.disp || 1);
      if (p > 0 && (!franja || p > franja.pct)) franja = { desde: a.hora, pct: Math.min(1, p) };
    }

    const abiertas = porHora.filter((p) => p.abierta);
    const tranquila =
      abiertas.length > 1 ? abiertas.reduce((b, p) => (p.pct < b.pct ? p : b)) : null;

    return {
      valor,
      pct,
      trabajadores,
      abierto,
      columnas,
      horas,
      totalMinutos,
      pico: pico as { dia: number; hora: number; pct: number } | null,
      franja,
      tranquila,
      hayHorario,
    };
  }, [celdas, horario, diasPeriodo, capacidad]);

  const esEmpresa = !usuarioFiltro;

  const porcentaje = (p: number) => `${Math.round(p * 100)} %`;

  const duracion = (min: number) => {
    const h = Math.floor(min / 60);
    const m = Math.round(min % 60);
    if (h === 0) return `${m} min`;
    return m === 0 ? `${h} h` : `${h} h ${m} min`;
  };

  const textoPeriodo =
    periodo === 1
      ? `${nombreMes(mes).toLowerCase()}`
      : t('dashboardCharts.lastThreeMonths');

  const subtitulo =
    periodo === 1
      ? `${nombreMes(mes)} ${anio}`
      : `${nombreMes(mes - 2)} – ${nombreMes(mes)}`;

  const colorCelda = (d: number, h: number): React.CSSProperties => {
    if (!datos.abierto(d, h)) {
      return {
        backgroundImage: `repeating-linear-gradient(135deg, ${
          dark ? 'rgba(255,255,255,0.07)' : 'rgba(16,50,84,0.08)'
        } 0 3px, transparent 3px 7px)`,
      };
    }
    const p = datos.pct(d, h);
    if (p === 0) {
      return { background: dark ? 'rgba(255,255,255,0.05)' : 'rgba(16,50,84,0.06)' };
    }
    return { background: colors.bar, opacity: 0.15 + p * 0.85 };
  };

  const activar = (e: React.SyntheticEvent<HTMLElement>, dia: number, hora: number) => {
    const cont = contRef.current?.getBoundingClientRect();
    const r = e.currentTarget.getBoundingClientRect();
    if (!cont) return;
    setActiva({
      dia,
      hora,
      x: r.left - cont.left + r.width / 2,
      y: r.top - cont.top,
    });
  };

  const celda = (dia: number, hora: number) => {
    const esActiva = activa?.dia === dia && activa?.hora === hora;
    const esPico = datos.pico?.dia === dia && datos.pico?.hora === hora;
    return (
      <button
        key={`${dia}-${hora}`}
        type="button"
        onPointerEnter={(e) => {
          if (e.pointerType === 'mouse') activar(e, dia, hora);
        }}
        onClick={(e) => activar(e, dia, hora)}
        aria-label={`${nombreDia(dia)} ${hh(hora)}`}
        className={`relative h-7 sm:h-8 rounded-md focus:outline-none touch-manipulation ${
          esActiva
            ? 'ring-2 ring-[#2170b8] ring-offset-1 ring-offset-white dark:ring-offset-zinc-900 z-10'
            : ''
        }`}
      >
        <span className="absolute inset-0 rounded-md pointer-events-none" style={colorCelda(dia, hora)} />
        {esPico && (
          <Flame className="relative w-3.5 h-3.5 mx-auto text-white drop-shadow pointer-events-none" />
        )}
      </button>
    );
  };

  const tooltip = (() => {
    if (!activa) return null;
    const { dia, hora } = activa;
    const cerrado = !datos.abierto(dia, hora);
    const minutosOcupados = datos.valor(dia, hora);
    const p = datos.pct(dia, hora);
    return { dia, hora, cerrado, minutosOcupados, p, n: datos.trabajadores(dia, hora) };
  })();

  const detalle = tooltip && (
    <>
      <div className="font-semibold">
        {nombreDia(tooltip.dia)} ·{' '}
        {t('dashboardCharts.fromTo', {
          desde: hh(tooltip.hora),
          hasta: hh(tooltip.hora + 1),
        })}
      </div>
      {tooltip.cerrado ? (
        <div className="mt-0.5 text-gray-500 dark:text-gray-400">
          {t('dashboardCharts.closed')}
        </div>
      ) : (
        <>
          <div className="mt-0.5">
            {tooltip.minutosOcupados === 0 ? (
              t('dashboardCharts.noAppointmentsHere')
            ) : (
              <>
                {t('dashboardCharts.busyPct')}{' '}
                <strong className="tabular-nums">{porcentaje(tooltip.p)}</strong>{' '}
                {t('dashboardCharts.ofTheTime')}
              </>
            )}
          </div>
          {tooltip.minutosOcupados > 0 && (
            <div className="text-gray-500 dark:text-gray-400">
              {t('dashboardCharts.bookedTime', {
                duracion: duracion(tooltip.minutosOcupados),
                periodo: textoPeriodo,
              })}
            </div>
          )}
          {esEmpresa && tooltip.n > 0 && (
            <div className="text-gray-500 dark:text-gray-400">
              {tooltip.n === 1
                ? t('dashboardCharts.oneWorker')
                : t('dashboardCharts.amongWorkers', { n: tooltip.n })}
            </div>
          )}
        </>
      )}
    </>
  );

  return (
    <Card
      className={`relative isolate rounded-2xl ${GLASS_CARD} tour-horas-pico md:col-span-2 transition-shadow duration-300 hover:shadow-[0_12px_32px_-12px_rgba(16,50,84,0.35)] dark:hover:shadow-[0_12px_32px_-12px_rgba(0,0,0,0.6)]`}
    >
      <CardContent
        className={`p-4 sm:p-6 space-y-4 transition-opacity duration-200 ${loading ? 'opacity-60' : ''}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 tracking-[0.06em]">
              {t('dashboardCharts.peakHours')}
            </h2>
            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
              {subtitulo} · {totalCitas} {t('dashboardCharts.appointments')}
            </p>
          </div>
          <div className="relative flex shrink-0 rounded-xl bg-gray-100/80 dark:bg-white/5 p-1">
            {([1, 3] as Periodo[]).map((p) => (
              <button
                key={p}
                onClick={() => {
                  setPeriodo(p);
                  setActiva(null);
                }}
                className={`relative z-10 px-2.5 py-1 text-[11px] font-semibold rounded-lg transition-colors ${
                  periodo === p
                    ? 'text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {periodo === p && (
                  <motion.span
                    layoutId="periodo-horas"
                    className="absolute inset-0 -z-10 rounded-lg bg-[#103254] dark:bg-[#2170b8] shadow"
                    transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                  />
                )}
                {p === 1 ? t('dashboardCharts.month') : t('dashboardCharts.threeMonths')}
              </button>
            ))}
          </div>
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
          {t('dashboardCharts.peakExplain')}
        </p>

        {filtrarTrabajadores && trabajadores.length > 1 && (
          <div className="tour-horas-trabajadores flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1" data-no-swipe>
            {[{ id: null as string | null, nombre: t('dashboardCharts.allWorkers') }, ...trabajadores].map(
              (tr) => {
                const activo = trabajadorSel === tr.id;
                return (
                  <button
                    key={tr.id ?? 'todos'}
                    type="button"
                    onClick={() => {
                      setTrabajadorSel(tr.id);
                      setActiva(null);
                    }}
                    className={`shrink-0 px-3 py-1 rounded-full text-[11px] font-semibold transition-colors whitespace-nowrap ${
                      activo
                        ? 'bg-[#103254] text-white dark:bg-[#2170b8]'
                        : 'bg-gray-100/80 text-gray-600 hover:bg-gray-200/80 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10'
                    }`}
                  >
                    {tr.nombre}
                  </button>
                );
              }
            )}
          </div>
        )}

        {error ? (
          <div className="h-40 flex items-center justify-center text-sm text-red-500 dark:text-red-400 text-center px-4">
            {t('dashboardCharts.peakError')}
          </div>
        ) : datos.totalMinutos === 0 ? (
          <div className="h-40 flex items-center justify-center text-sm text-gray-400 dark:text-gray-500">
            {t('dashboardCharts.noPeakData')}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {datos.pico && (
                <Dato
                  icon={<Flame className="w-4 h-4 text-orange-500" />}
                  label={t('dashboardCharts.peakSlot')}
                  value={`${nombreDia(datos.pico.dia)} ${t('dashboardCharts.at')} ${hh(datos.pico.hora)} · ${porcentaje(datos.pico.pct)}`}
                />
              )}
              {datos.franja && (
                <Dato
                  icon={<Sunrise className="w-4 h-4 text-amber-500" />}
                  label={t('dashboardCharts.busiestWindow')}
                  value={`${t('dashboardCharts.fromTo', {
                    desde: hh(datos.franja.desde),
                    hasta: hh(datos.franja.desde + 2),
                  })} · ${porcentaje(datos.franja.pct)}`}
                />
              )}
              {datos.tranquila && (
                <Dato
                  icon={<Moon className="w-4 h-4 text-indigo-400" />}
                  label={t('dashboardCharts.quietestHour')}
                  value={`${t('dashboardCharts.fromTo', {
                    desde: hh(datos.tranquila.hora),
                    hasta: hh(datos.tranquila.hora + 1),
                  })} · ${porcentaje(datos.tranquila.pct)}`}
                />
              )}
            </div>

            <div
              ref={contRef}
              data-no-swipe
              className="relative"
              onMouseLeave={() => !isMobile && setActiva(null)}
            >
              {isMobile ? (
                <div
                  className="grid gap-1 items-center"
                  style={{ gridTemplateColumns: '2.8rem repeat(7, minmax(0, 1fr))' }}
                >
                  <span />
                  {DIAS.map((d) => (
                    <span
                      key={d}
                      className="text-center text-[10px] font-semibold text-gray-500 dark:text-gray-400"
                    >
                      {CLAVES_DIA[d - 1]}
                    </span>
                  ))}
                  {datos.columnas.map((c) =>
                    c.tipo === 'descanso' ? (
                      <div
                        key={`d-${c.desde}`}
                        className="col-span-8 flex items-center gap-2 py-1 text-[10px] text-gray-400 dark:text-gray-500"
                      >
                        <span className="flex-1 border-t border-dashed border-gray-300 dark:border-white/15" />
                        <Coffee className="w-3 h-3" />
                        {t('dashboardCharts.breakTime', { desde: hh(c.desde), hasta: hh(c.hasta) })}
                        <span className="flex-1 border-t border-dashed border-gray-300 dark:border-white/15" />
                      </div>
                    ) : (
                      <div key={c.hora} className="contents">
                        <span className="text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">
                          {hh(c.hora)}
                        </span>
                        {DIAS.map((d) => celda(d, c.hora))}
                      </div>
                    )
                  )}
                </div>
              ) : (
                <div
                  className="grid gap-1 items-center"
                  style={{
                    gridTemplateColumns: [
                      '2.6rem',
                      ...datos.columnas.map((c) =>
                        c.tipo === 'descanso' ? '1.75rem' : 'minmax(0, 1fr)'
                      ),
                    ].join(' '),
                  }}
                >
                  {DIAS.map((d) => (
                    <div key={d} className="contents">
                      <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">
                        {nombreDiaCorto(d)}
                      </span>
                      {datos.columnas.map((c) =>
                        c.tipo === 'descanso' ? (
                          <span
                            key={`d-${c.desde}`}
                            className="h-8 flex justify-center"
                            title={t('dashboardCharts.breakTime', {
                              desde: hh(c.desde),
                              hasta: hh(c.hasta),
                            })}
                          >
                            <span className="h-full border-l border-dashed border-gray-300 dark:border-white/15" />
                          </span>
                        ) : (
                          celda(d, c.hora)
                        )
                      )}
                    </div>
                  ))}
                  <span />
                  {datos.columnas.map((c) =>
                    c.tipo === 'descanso' ? (
                      <span key={`dl-${c.desde}`} className="flex justify-center pt-1">
                        <Coffee className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                      </span>
                    ) : (
                      <span
                        key={`l-${c.hora}`}
                        className="text-center text-[10px] text-gray-400 dark:text-gray-500 tabular-nums pt-1"
                      >
                        {hh(c.hora)}
                      </span>
                    )
                  )}
                </div>
              )}

              {!isMobile && (
                <AnimatePresence>
                  {activa && tooltip && (
                    <motion.div
                      key={`${tooltip.dia}-${tooltip.hora}`}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.12 }}
                      className="pointer-events-none absolute z-20 rounded-xl px-3 py-2 text-xs shadow-lg w-max max-w-[240px]"
                      style={{
                        left: Math.min(
                          Math.max(activa.x, 120),
                          (contRef.current?.clientWidth ?? 0) - 120
                        ),
                        top: activa.y - 8,
                        transform: 'translate(-50%, -100%)',
                        background: colors.tooltipBg,
                        border: `1px solid ${colors.tooltipBorder}`,
                        color: colors.tooltipText,
                      }}
                    >
                      {detalle}
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>

            {isMobile && (
              <div className="rounded-xl bg-gray-50/80 dark:bg-white/5 px-3 py-2.5 text-xs text-gray-700 dark:text-gray-200 min-h-[58px]">
                {tooltip ? (
                  detalle
                ) : (
                  <span className="text-gray-400 dark:text-gray-500">
                    {t('dashboardCharts.tapHint')}
                  </span>
                )}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-1.5">
                <span>0 %</span>
                {[0.15, 0.36, 0.58, 0.79, 1].map((o) => (
                  <span
                    key={o}
                    className="w-3.5 h-3.5 rounded-[4px]"
                    style={{ background: colors.bar, opacity: o }}
                  />
                ))}
                <span>100 % {t('dashboardCharts.busy')}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Flame className="w-3.5 h-3.5 text-orange-500" />
                {t('dashboardCharts.peakSlot')}
              </div>
              {datos.hayHorario && (
                <div className="flex items-center gap-1.5">
                  <span
                    className="w-3.5 h-3.5 rounded-[4px]"
                    style={colorCelda(0, -99)}
                  />
                  {t('dashboardCharts.closed')}
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DashboardHorasPico;
